import clsx from "clsx";
import { useState } from "react";
import { ChevronDown } from "react-bootstrap-icons";
import { ReportingTimeBucket, ReportType } from "@/briefpoint-client";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import styles from "./RunReportButtonGroup.module.scss";

const REPORT_OPTIONS: {
  label: string;
  reportType: ReportType;
  bucket: ReportingTimeBucket;
}[] = [
  {
    label: "Usage Report - By Week",
    reportType: ReportType.Usage,
    bucket: ReportingTimeBucket.Week,
  },
  {
    label: "Usage Report - By Month",
    reportType: ReportType.Usage,
    bucket: ReportingTimeBucket.Month,
  },
];

export type RunReportButtonGroupProps = {
  onGenerateReport: (
    reportType: ReportType,
    bucket: ReportingTimeBucket,
  ) => Promise<void>;
  /** Primary button runs this report (default: monthly usage). */
  defaultReportType?: ReportType;
  defaultBucket?: ReportingTimeBucket;
};

export default function RunReportButtonGroup({
  onGenerateReport,
  defaultReportType = ReportType.Usage,
  defaultBucket = ReportingTimeBucket.Month,
}: RunReportButtonGroupProps) {
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const run = async (reportType: ReportType, bucket: ReportingTimeBucket) => {
    setLoading(true);
    try {
      await onGenerateReport(reportType, bucket);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ButtonGroup aria-label="Usage report">
      <Button
        type="button"
        size="lg"
        variant="outline"
        onClick={() => run(defaultReportType, defaultBucket)}
        disabled={loading}
      >
        {loading ? "Generating..." : "Usage Report"}
      </Button>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className={styles.caret}
            disabled={loading}
            aria-label="Report options"
          >
            <span
              className={clsx(styles.caretIcon, {
                [styles.caretIconOpen]: dropdownOpen,
              })}
            >
              <ChevronDown />
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={styles.dropdownContent}>
          {REPORT_OPTIONS.map(({ label, reportType, bucket }) => (
            <DropdownMenuItem
              key={bucket}
              disabled={loading}
              onSelect={() => run(reportType, bucket)}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
}
